import React, {createContext, useContext, useMemo, useReducer} from 'react';
import {AvailabilityObject, UserDetails} from '../../../types/schemas/abc';
import { CalendarEvent } from '../../../hooks/useEvents';
import { v4 as uuidv4 } from 'uuid';
import { getFirstName } from '../../../utils/name';

export interface BusySlot {
    startAt: string;
    endAt: string;
}

export const OTHER = '__other__';

const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export type Frequency = 'daily' | 'weekly' | 'biweekly' | 'monthly';
export type Step = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface LessonItem {
    id: string; 
    date: string; 
    time: string; 
    endTime: string; 
    title: string; 
    customTitle?: boolean;
}

export interface LessonCreationState {
    user: UserDetails;
    tutorId: string | null;
    tutorName: string | null;
    tutorHourlyRate: number;
    tutorTimezone: string;
    tutorAvailability: AvailabilityObject | null;
    existingEvents: CalendarEvent[];
    tutorBusySlots: BusySlot[];
    activeInputTz: string; // timezone used for time inputs (parent's home tz or browser tz)

    // step 0
    introMeetingChoice: 'yes' | 'no' | null;
    introDate: string; 
    introTime: string; 
    eventName: string;

    // Step 1
    subjectChoice: string | null;
    levelChoice: string | null;
    subjectOther: string;
    levelOther: string;

    // Step 2
    lessonDate: string;
    lessonTime: string;
    lessonEndTime: string;
    lessonTitle: string; // Base title

    // step 3
    recursChoice: 'yes' | 'no' | null;

    // Step 4
    frequency: Frequency | null;
    occurrenceCount: number;

    // Step 5
    lessons: LessonItem[];
    seriesName: string;
    isEditingSeriesName: boolean;
    editingLessonId: string | null;

    // Step 6
    clientSecret: string;
    totalAmount: number;
    pendingLessonIds: string[];

}

export const getInitialState = (
    user: UserDetails,
    tutorId: string | null,
    tutorName: string | null,
    tutorHourlyRate: number,
    tutorTimezone: string = 'Europe/London',
    tutorAvailability: AvailabilityObject | null = null,
    existingEvents: CalendarEvent[] = [],
    tutorBusySlots: BusySlot[] = [],
) : LessonCreationState => ({
    user,
    tutorId,
    tutorName,
    tutorHourlyRate,
    tutorTimezone,
    tutorAvailability,
    existingEvents,
    tutorBusySlots,
    activeInputTz: user.timezone || 'Europe/London',

    introMeetingChoice: null,
    introDate: getTodayString(),
    introTime: '',
    eventName: '',

    subjectChoice: null,
    levelChoice: null,
    subjectOther: '',
    levelOther: '',

    lessonDate: getTodayString(),
    lessonTime: '',
    lessonEndTime: '',
    lessonTitle: '',

    recursChoice: null,
    frequency: null,
    occurrenceCount: 4,

    lessons: [],
    seriesName: '',
    isEditingSeriesName: false,
    editingLessonId: null,

    clientSecret: '',
    totalAmount: 0,
    pendingLessonIds: [],

})

export type LessonCreationAction = 
  | {type: 'SET_INTRO_CHOICE'; payload: 'yes' | 'no' | null}
  | { type: 'SET_INTRO_DATE'; payload: string }
  | { type: 'SET_INTRO_TIME'; payload: string }
  | { type: 'SET_EVENT_NAME'; payload: string }
  | { type: 'SET_SUBJECT_CHOICE'; payload: string | null }
  | { type: 'SET_LEVEL_CHOICE'; payload: string | null }
  | { type: 'SET_SUBJECT_OTHER'; payload: string }
  | { type: 'SET_LEVEL_OTHER'; payload: string }
  | { type: 'SET_LESSON_DATE'; payload: string }
  | { type: 'SET_LESSON_TIME'; payload: string }
  | { type: 'SET_LESSON_END_TIME'; payload: string }
  | { type: 'SET_LESSON_TITLE'; payload: string }
  | { type: 'SET_RECURS_CHOICE'; payload: 'yes' | 'no' | null }
  | { type: 'SET_FREQUENCY'; payload: Frequency | null }
  | { type: 'SET_OCCURRENCE_COUNT'; payload: number }
  | { type: 'SET_LESSONS'; payload: LessonItem[] }
  | { type: 'UPDATE_LESSON'; payload: { id: string; patch: Partial<LessonItem> } }
  | { type: 'DELETE_LESSON'; payload: string }
  | { type: 'SET_SERIES_NAME'; payload: string }
  | { type: 'SET_IS_EDITING_SERIES_NAME'; payload: boolean }
  | { type: 'SET_EDITING_LESSON_ID'; payload: string | null }
  | { type: 'SET_CLIENT_SECRET'; payload: string }
  | { type: 'SET_TOTAL_AMOUNT'; payload: number }
  | { type: 'SET_PENDING_LESSON_IDS'; payload: string[] }
  | { type: 'RESET'; payload: LessonCreationState }
  | { type: 'SET_TUTOR_HOURLY_RATE'; payload: number }
  | { type: 'SET_TUTOR_BUSY_SLOTS'; payload: BusySlot[] }
  | { type: 'SET_ACTIVE_INPUT_TZ'; payload: string };

  export const lessonCreationReducer = (
    state: LessonCreationState, 
    action: LessonCreationAction,
  ) : LessonCreationState => {
    switch (action.type) {
        case 'SET_INTRO_CHOICE': 
            return {...state, introMeetingChoice: action.payload}
        case 'SET_INTRO_DATE':
            return { ...state, introDate: action.payload };
        case 'SET_INTRO_TIME':
            return { ...state, introTime: action.payload };
        case 'SET_EVENT_NAME':
            return { ...state, eventName: action.payload };
        
        case 'SET_SUBJECT_CHOICE':
            return {
                ...state,
                subjectChoice: action.payload,
                // If user picks a real subject, clear the 'other' field
                ...(action.payload !== OTHER && { subjectOther: '' }),
            };
        case 'SET_LEVEL_CHOICE':
            return {
                ...state,
                levelChoice: action.payload,
                ...(action.payload !== OTHER && { levelOther: '' }),
            };
        case 'SET_SUBJECT_OTHER':
            return { ...state, subjectOther: action.payload };
        case 'SET_LEVEL_OTHER':
            return { ...state, levelOther: action.payload };
        case 'SET_LESSON_DATE':
            return { ...state, lessonDate: action.payload };
        case 'SET_LESSON_TIME':
            return { ...state, lessonTime: action.payload };
        case 'SET_LESSON_END_TIME':
            return { ...state, lessonEndTime: action.payload };
        case 'SET_LESSON_TITLE':
            return { ...state, lessonTitle: action.payload };
        
        case 'SET_RECURS_CHOICE':
            return { ...state, recursChoice: action.payload };
        case 'SET_FREQUENCY':
            return { ...state, frequency: action.payload };
        case 'SET_OCCURRENCE_COUNT':
            return { ...state, occurrenceCount: action.payload };
        
        case 'SET_LESSONS':
            return { ...state, lessons: action.payload };
        case 'UPDATE_LESSON':
            return {
                ...state,
                lessons: state.lessons.map(l =>
                l.id === action.payload.id ? { ...l, ...action.payload.patch } : l,
                ),
            };
        case 'DELETE_LESSON': {
            const remaining = state.lessons.filter(l => l.id !== action.payload);
            // Renumber "Lesson N:" prefixes so ordering stays sequential
            const renumbered = remaining.map((l, i) => {
                if (!l.customTitle && remaining.length > 1) {
                    const base = l.title.replace(/^Lesson \d+:\s*/, '');
                    return { ...l, title: `Lesson ${i + 1}: ${base}` };
                }
                return l;
            });
            return {
                ...state,
                lessons: renumbered,
                ...(state.editingLessonId === action.payload && { editingLessonId: null }),
            };
        }
        case 'SET_SERIES_NAME':
            return { ...state, seriesName: action.payload };
        case 'SET_IS_EDITING_SERIES_NAME':
            return { ...state, isEditingSeriesName: action.payload };
        case 'SET_EDITING_LESSON_ID':
            return { ...state, editingLessonId: action.payload };
            
        case 'SET_CLIENT_SECRET':
            return { ...state, clientSecret: action.payload };
        case 'SET_TOTAL_AMOUNT':
            return { ...state, totalAmount: action.payload };
        case 'SET_PENDING_LESSON_IDS':
            return { ...state, pendingLessonIds: action.payload };
        case 'RESET':
            return { ...action.payload }; 
        case 'SET_TUTOR_HOURLY_RATE':
            return {...state, tutorHourlyRate: action.payload};
        case 'SET_TUTOR_BUSY_SLOTS':
            return {...state, tutorBusySlots: action.payload};
        case 'SET_ACTIVE_INPUT_TZ':
            return {...state, activeInputTz: action.payload};

        default:
            return state;   
    }
  }

  const LessonCreationStateContext = createContext<LessonCreationState | undefined>(undefined);
  const LessonCreationDispatchContext = createContext<React.Dispatch<LessonCreationAction> | undefined>(undefined);

  interface LessonCreationProviderProps {
    children: React.ReactNode;
    user: UserDetails;
    tutorId: string | null;
    tutorName: string | null;
    tutorHourlyRate: number;
    tutorTimezone: string;
    tutorAvailability: AvailabilityObject | null;
    existingEvents: CalendarEvent[];
  }

  export const LessonCreationProvider: React.FC<LessonCreationProviderProps> = ({
    children,
    user,
    tutorId,
    tutorName,
    tutorHourlyRate,
    tutorTimezone,
    tutorAvailability,
    existingEvents,
  }) => {
    const initialState = getInitialState(user, tutorId, getFirstName(tutorName) || null, tutorHourlyRate, tutorTimezone, tutorAvailability, existingEvents);
    const [state, dispatch] = useReducer(lessonCreationReducer, initialState)
    const stateValue = useMemo(() => state, [state]);

    return (
        <LessonCreationStateContext.Provider value={stateValue}>
        <LessonCreationDispatchContext.Provider value={dispatch}>
        {children}
        </LessonCreationDispatchContext.Provider>
        </LessonCreationStateContext.Provider> 
    );
};

export const useLessonCreationState = (): LessonCreationState => {
    const context = useContext(LessonCreationStateContext);
    if(context === undefined) {
        throw new Error('useLessonCreationState must be used within a LessonCreationProvider');
    }
    return context; 
}

export const useLessonCreationDispatch = (): React.Dispatch<LessonCreationAction> => {
    const context = useContext(LessonCreationDispatchContext);
    if (context === undefined) {
        throw new Error('useLessonCreationState must be used within a LessonCreationProvider');
    } 
    return context;
}

